import React from 'react';
import { BarChart, PieChart, ChevronDown, ChevronUp } from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика состава тела
 */
const BodyCompositionChart = ({ results }) => {
  const [showDetails, setShowDetails] = React.useState(false);
  
  if (!results) return null;

  // Данные для графика состава тела
  const bodyCompositionData = [
    { name: 'Жировая масса', value: results.bodyComposition.fatMass, color: '#f97316' }, // orange-500
    { name: 'Безжировая масса', value: results.bodyComposition.leanMass, color: '#059669' }  // emerald-600
  ];

  // Данные для сравнения с оптимальными значениями
  const optimalFatMass = (results.weight * results.optimalRange.min) / 100;
  const optimalLeanMass = results.weight - optimalFatMass;
  const excessFatMass = Math.max(0, results.bodyComposition.fatMass - optimalFatMass);

  // Данные для сравнительного графика (отображается в расширенном режиме)
  const comparisonData = [
    { 
      name: 'Текущий состав', 
      fat: results.bodyComposition.fatMass, 
      lean: results.bodyComposition.leanMass
    },
    { 
      name: 'Оптимальный состав', 
      fat: optimalFatMass,
      lean: results.bodyComposition.leanMass
    }
  ];

  // Кастомный тултип для графика
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-md">
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-sm">
            {formatNumber(payload[0].value, 1)} кг 
            <span className="text-gray-500 ml-1">
              ({formatNumber((payload[0].value / results.weight) * 100, 1)}%)
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-emerald-500" />
          Визуализация состава тела
        </h3>
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 flex items-center"
        >
          {showDetails ? 'Скрыть детали' : 'Показать детали'}
          {showDetails ? (
            <ChevronUp className="w-4 h-4 ml-1" />
          ) : (
            <ChevronDown className="w-4 h-4 ml-1" />
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        {/* График состава тела */}
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={bodyCompositionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                innerRadius={showDetails ? 40 : 0}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
              >
                {bodyCompositionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>

        {/* Подробная информация о составе тела */}
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Состав тела
            </h4>
            
            <div className="space-y-2">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Жировая масса:</span>
                  <span className="font-medium">
                    {formatNumber(results.bodyComposition.fatMass, 1)} кг
                    <span className="text-orange-500 ml-1">
                      ({formatNumber(results.bodyFatPercentage, 1)}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full"
                    style={{ width: `${results.bodyFatPercentage}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Безжировая масса:</span>
                  <span className="font-medium">
                    {formatNumber(results.bodyComposition.leanMass, 1)} кг
                    <span className="text-emerald-500 ml-1">
                      ({formatNumber(100 - results.bodyFatPercentage, 1)}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full"
                    style={{ width: `${100 - results.bodyFatPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Оптимальные значения */}
          <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-100 dark:border-emerald-800">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Сравнение с оптимальными значениями
            </h4>
            
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Оптимальный % жира:</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  {results.optimalRange.min}–{results.optimalRange.max}%
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Оптимальная жировая масса:</span>
                <span className="font-medium">
                  {formatNumber(optimalFatMass, 1)} кг
                </span>
              </div>
              
              {excessFatMass > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Избыточная жировая масса:</span>
                  <span className="font-medium text-orange-500 dark:text-orange-400">
                    {formatNumber(excessFatMass, 1)} кг
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Дополнительная информация при развернутом режиме */}
      {showDetails && (
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Расшифровка компонентов состава тела
          </h4>
          
          <div className="space-y-4 text-sm">
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <h5 className="font-medium text-emerald-600 dark:text-emerald-400 mb-1">
                Безжировая масса тела ({formatNumber(results.bodyComposition.leanMass, 1)} кг)
              </h5>
              <p className="text-gray-600 dark:text-gray-400">
                Включает мышцы, кости, внутренние органы, воду и все другие нежировые ткани. 
                Это активная метаболическая масса, которая потребляет энергию.
              </p>
            </div>
            
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <h5 className="font-medium text-orange-600 dark:text-orange-400 mb-1">
                Жировая масса ({formatNumber(results.bodyComposition.fatMass, 1)} кг)
              </h5>
              <p className="text-gray-600 dark:text-gray-400">
                Включает необходимый жир (~3-5% у мужчин, ~8-12% у женщин) и запасной жир. Определенный уровень 
                жира необходим для здоровья, но избыточный жир может привести к проблемам со здоровьем.
              </p>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h5 className="font-medium text-blue-600 dark:text-blue-400 mb-1">
                Как улучшить состав тела
              </h5>
              <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1">
                <li>Силовые тренировки для сохранения и увеличения мышечной массы</li>
                <li>Кардио и высокоинтенсивные тренировки для снижения жировой массы</li>
                <li>Сбалансированное питание с достаточным количеством белка</li>
                <li>Постепенное снижение веса (не более 0.5-1 кг в неделю)</li>
                <li>Регулярный сон и контроль стресса</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BodyCompositionChart;